import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { UserRound, Save, Loader2, Mail } from "lucide-react";
import FamilyAccessCard from "./FamilyAccessCard";

const CONTACT = ["email", "phone", "text"];

export default function ClientProfile() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/portal/client/profile").then(({ data }) => setP(data.profile)).catch(() => {}); }, []);
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/portal/client/profile", {
        full_name: p.full_name, phone: p.phone, relationship_to_loved_one: p.relationship_to_loved_one,
        city: p.city, state: p.state, country: p.country, preferred_contact_method: p.preferred_contact_method,
      });
      setP(data.profile); toast.success("Profile saved.");
    } catch (e) { toast.error(errMsg(e, "Could not save your profile.")); }
    finally { setSaving(false); }
  };

  if (!p) return <div className="max-w-xl space-y-4"><Skeleton className="h-40" /></div>;

  return (
    <div className="max-w-xl" data-testid="client-profile-page">
      <h2 className="font-display text-2xl font-bold text-[#252221] flex items-center gap-2 mb-1"><UserRound size={22} /> Your profile</h2>
      <p className="text-[15px] text-muted-foreground mb-5">Keep your details current so your guide can reach you the way you prefer.</p>
      <Card className="p-6 border-[#E6EDF2] space-y-4">
        <div>
          <Label className="text-[13px] text-[#4B5563]">Full name</Label>
          <Input value={p.full_name || ""} onChange={(e) => set("full_name", e.target.value)} className="mt-1 h-11 text-[15px]" data-testid="profile-name" />
        </div>
        <div>
          <Label className="text-[13px] text-[#4B5563]">Email</Label>
          <div className="mt-1 h-11 rounded-md border border-[#EEF2F5] bg-[#F4F8FB] px-3 flex items-center gap-2 text-[15px] text-[#6B7280]" data-testid="profile-email"><Mail size={15} /> {p.email}</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[13px] text-[#4B5563]">Phone</Label>
            <Input value={p.phone || ""} onChange={(e) => set("phone", e.target.value)} className="mt-1 h-11 text-[15px]" data-testid="profile-phone" />
          </div>
          <div>
            <Label className="text-[13px] text-[#4B5563]">You are the loved one's…</Label>
            <Input value={p.relationship_to_loved_one || ""} onChange={(e) => set("relationship_to_loved_one", e.target.value)} className="mt-1 h-11 text-[15px]" data-testid="profile-relationship" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-[13px] text-[#4B5563]">City</Label><Input value={p.city || ""} onChange={(e) => set("city", e.target.value)} className="mt-1 h-11 text-[15px]" data-testid="profile-city" /></div>
          <div><Label className="text-[13px] text-[#4B5563]">State</Label><Input value={p.state || ""} onChange={(e) => set("state", e.target.value)} className="mt-1 h-11 text-[15px]" data-testid="profile-state" /></div>
          <div><Label className="text-[13px] text-[#4B5563]">Country</Label><Input value={p.country || ""} onChange={(e) => set("country", e.target.value)} className="mt-1 h-11 text-[15px]" data-testid="profile-country" /></div>
        </div>
        <div>
          <Label className="text-[13px] text-[#4B5563]">Preferred contact method</Label>
          <select value={p.preferred_contact_method || "email"} onChange={(e) => set("preferred_contact_method", e.target.value)} className="mt-1 w-full h-11 rounded-md border border-[#D6E0E7] px-2 text-[15px] bg-white capitalize" data-testid="profile-contact-method">
            {CONTACT.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex justify-end pt-1">
          <Button onClick={save} disabled={saving} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1.5 h-11 rounded-full px-6" data-testid="profile-save-btn">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save profile
          </Button>
        </div>
      </Card>
      <FamilyAccessCard />
    </div>
  );
}
